import React from 'react';
import { motion } from 'framer-motion';
import {
    Box,
    Container,
    Typography,
    Button,
    Grid,
    useTheme,
    alpha,
    Card,
    CardContent
} from '@mui/material';
import {
    Apple as AppleIcon,
    Android as AndroidIcon,
    Smartphone as MobileIcon,
    Security as SecurityIcon,
    Speed as SpeedIcon,
    SupportAgent as SupportIcon
} from '@mui/icons-material';

const Mobile = () => {
    const theme = useTheme();

    const features = [
        {
            icon: <SpeedIcon sx={{ color: theme.palette.primary.main, fontSize: 30 }} />,
            title: 'Fast Downloads',
            description: 'Quick and efficient app installation'
        },
        {
            icon: <SecurityIcon sx={{ color: theme.palette.primary.main, fontSize: 30 }} />,
            title: 'Secure Payments',
            description: 'Bank-level security for all transactions'
        },
        {
            icon: <SupportIcon sx={{ color: theme.palette.primary.main, fontSize: 30 }} />,
            title: '24/7 Support',
            description: 'Round-the-clock customer assistance'
        }
    ];

    // Motion variants
    const fadeUp = {
        hidden: { opacity: 0, y: 40 },
        visible: i => ({ opacity: 1, y: 0, transition: { delay: i * 0.2, duration: 0.6 } })
    };

    const fadeIn = { hidden: { opacity: 0 }, visible: { opacity: 1, transition: { duration: 0.8 } } };

    return (
        <Box
            sx={{
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.primary.light, 0.15)} 100%)`,
                py: { xs: 8, md: 12 },
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: '-10%',
                    right: '-10%',
                    width: '300px',
                    height: '300px',
                    borderRadius: '50%',
                    background: `radial-gradient(${alpha(theme.palette.primary.main, 0.1)} 0%, transparent 70%)`,
                    zIndex: 0
                },
                '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: '-10%',
                    left: '-10%',
                    width: '250px',
                    height: '250px',
                    borderRadius: '50%',
                    background: `radial-gradient(${alpha(theme.palette.primary.main, 0.1)} 0%, transparent 70%)`,
                    zIndex: 0
                }
            }}
        >
            <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
                <Grid container spacing={4} alignItems="center" justifyContent="center">
                    {/* Left Section */}
                    <Grid item xs={12} md={6}>
                        <motion.div initial="hidden" animate="visible" variants={fadeIn}>
                            <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
                                <Typography
                                    variant="h3"
                                    component="h2"
                                    gutterBottom
                                    sx={{
                                        fontWeight: 'bold',
                                        mb: 2,
                                        color: theme.palette.primary.dark,
                                        fontSize: { xs: '2rem', md: '2.5rem' }
                                    }}
                                >
                                    Get Started with{' '}
                                    <Box
                                        component="span"
                                        sx={{
                                            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                                            backgroundClip: 'text',
                                            WebkitBackgroundClip: 'text',
                                            color: 'red',
                                        }}
                                    >
                                        esPay
                                    </Box>{' '}
                                    for free
                                </Typography>
                                <Typography
                                    variant="h6"
                                    sx={{ color: theme.palette.text.secondary, mb: 4, fontWeight: 'normal' }}
                                >
                                    Start using esPay services today with our mobile app
                                </Typography>

                                <Box sx={{
                                    display: 'flex',
                                    flexDirection: { xs: 'column', sm: 'row' },
                                    gap: 2,
                                    justifyContent: { xs: 'center', md: 'flex-start' }
                                }}>
                                    <motion.div whileHover={{ y: -3 }} transition={{ type: 'spring', stiffness: 300 }}>
                                        <Button
                                            variant="contained"
                                            size="large"
                                            startIcon={<AppleIcon />}
                                            sx={{
                                                borderRadius: 3,
                                                px: 3,
                                                py: 1.5,
                                                fontWeight: 'bold',
                                                background: `linear-gradient(45deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                                                boxShadow: `0 4px 15px ${alpha(theme.palette.primary.main, 0.3)}`,
                                                '&:hover': {
                                                    background: `linear-gradient(45deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                                                },
                                                transition: 'all 0.3s ease-in-out'
                                            }}
                                        >
                                            App Store
                                        </Button>
                                    </motion.div>

                                    <motion.div whileHover={{ y: -3 }} transition={{ type: 'spring', stiffness: 300 }}>
                                        <Button
                                            variant="contained"
                                            size="large"
                                            startIcon={<AndroidIcon />}
                                            sx={{
                                                borderRadius: 3,
                                                px: 3,
                                                py: 1.5,
                                                fontWeight: 'bold',
                                                background: `linear-gradient(45deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                                                boxShadow: `0 4px 15px ${alpha(theme.palette.primary.main, 0.3)}`,
                                                '&:hover': {
                                                    background: `linear-gradient(45deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                                                },
                                                transition: 'all 0.3s ease-in-out'
                                            }}
                                        >
                                            Play Store
                                        </Button>
                                    </motion.div>
                                </Box>
                            </Box>
                        </motion.div>
                    </Grid>

                    {/* Right Section */}
                    <Grid item xs={12} md={6}>
                        <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                <Box sx={{
                                    position: 'relative',
                                    width: 250,
                                    height: 250,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}>
                                    <motion.div animate={{ scale: [1, 1.1, 1] }} transition={{ repeat: Infinity, duration: 2, ease: 'easeInOut' }}>
                                        <Box
                                            sx={{
                                                width: '100%',
                                                height: '100%',
                                                borderRadius: '50%',
                                                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}
                                        >
                                            <MobileIcon sx={{ fontSize: 120, color: theme.palette.primary.main }} />
                                        </Box>
                                    </motion.div>
                                </Box>
                            </Box>
                        </motion.div>
                    </Grid>
                </Grid>

                {/* Features */}
                <Grid container spacing={3} justifyContent="center" sx={{ mt: 8 }}>
                    {features.map((feature, index) => (
                        <Grid item xs={12} sm={6} md={4} key={index}>
                            <motion.div
                                custom={index}
                                initial="hidden"
                                whileInView="visible"
                                viewport={{ once: true }}
                                variants={fadeUp}
                                whileHover={{ y: -5, scale: 1.02 }}
                            >
                                <Card sx={{
                                    borderRadius: 3,
                                    boxShadow: `0 8px 20px ${alpha(theme.palette.primary.main, 0.1)}`,
                                    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                                    transition: 'all 0.3s ease',
                                }}>
                                    <CardContent sx={{ p: 3, textAlign: 'center' }}>
                                        <Box sx={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            width: 60,
                                            height: 60,
                                            borderRadius: '50%',
                                            backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                            mb: 2
                                        }}>
                                            {feature.icon}
                                        </Box>
                                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.primary.dark, mb: 1 }}>
                                            {feature.title}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                                            {feature.description}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </motion.div>
                        </Grid>
                    ))}
                </Grid>
            </Container>
        </Box>
    );
};

export default Mobile;
